import React, { useState, useEffect } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Nav,
  Tab,
  Badge,
  Alert,
  Card,
  InputGroup,
  ListGroup,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { useTranslation } from "react-i18next";
import { getCompanies } from "../../../features/companies/companiesSlice";
import {
  createChatbot,
  updateChatbot,
} from "../../../features/ai_chatbots/aiChatbotsSlice";
import {
  FaRobot,
  FaCog,
  FaBrain,
  FaDatabase,
  FaPalette,
  FaBuilding,
  FaUser,
  FaKey,
  FaGlobe,
  FaInfoCircle,
  FaCheckCircle,
  FaExclamationTriangle,
  FaSave,
  FaCheck,
  FaUndo,
  FaPlus,
  FaTimes,
  FaTrash,
} from "react-icons/fa";
import { Icon } from "../../../components/common/Icon";
import {
  getChatbotSettings,
  updateChatbotSettings,
  listKnowledgeSources,
  createKnowledgeSource,
  updateKnowledgeSource,
  deleteKnowledgeSource,
  ingestTextToSource,
  uploadSourceFile,
  ingestUploadedFile,
  listSourceChunks,
  searchPreview,
  getSourceContent,
  listSourceJobs,
} from "../../../features/ai_chatbots/aiChatbotsService";
import WidgetCustomizationBuilder from "./widget_settings/WidgetCustomizationBuilder";
import companiesService from "../../../features/companies/companiesService";

interface AIChatbotFormModalProps {
  show: boolean;
  onHide: () => void;
  chatbot?: any;
  onSuccess?: () => void;
  defaultCompanyId?: number;
  initialActiveTab?: string;
  defaultModule?: string;
}

const AIChatbotFormModal: React.FC<AIChatbotFormModalProps> = ({
  show,
  onHide,
  chatbot,
  onSuccess,
  defaultCompanyId,
  initialActiveTab,
  defaultModule,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const { user } = useSelector((state: RootState) => state.auth);
  const isEditMode = !!chatbot;
  const { companies } = useSelector((state: RootState) => state.companies);
  const [activeTab, setActiveTab] = useState(initialActiveTab || "general");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  // Training state (settings, sources, ingestion)
  const [trainingLoading, setTrainingLoading] = useState(false);
  const [trainingError, setTrainingError] = useState<string | null>(null);
  const [settingsData, setSettingsData] = useState<any>({
    assistant_profile: {
      persona: "",
      tone: "friendly",
      default_language: "en",
      reply_in_user_language: false,
      answer_format: "short_paragraphs",
      max_answer_tokens: 250,
    },
    retrieval: {
      top_k: 3,
      min_score: 0,
      snippet_chars: 400,
      chunk_size: 500,
      chunk_overlap: 60,
    },
    guardrails: {
      banned_topics: [],
      safety_policies: [],
      no_context_behavior: "ask_clarifying",
    },
    limits: {
      max_input_tokens: 1200,
      max_output_tokens: 300,
      rate_per_min: 30,
      cache_ttl_seconds: 3600,
    },
    provider: { name: "openai", model: "gpt-4o-mini" },
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [dirtySettings, setDirtySettings] = useState(false);
  const [saveSuccessAt, setSaveSuccessAt] = useState<number | null>(null);
  const tonePresets = [
    "friendly",
    "professional",
    "casual",
    "concise",
    "empathetic",
  ];
  const bannedSuggestionsByLang: Record<string, string[]> = {
    en: [
      "Personal Data",
      "Religion",
      "Racism",
      "Profanity",
      "Hate Speech",
      "Sexual Content",
      "Violence",
      "Medical Advice",
      "Legal Advice",
      "Financial Advice",
    ],
    tr: [
      "Kişisel Veri",
      "Din",
      "Irkçılık",
      "Argo",
      "Nefret Söylemi",
      "Cinsel İçerik",
      "Şiddet",
      "Tıbbi Tavsiye",
      "Hukuki Tavsiye",
      "Finansal Tavsiye",
    ],
  };
  const languages = [
    { code: "en", name: "English", flag: "🇬🇧" },
    { code: "tr", name: "Türkçe", flag: "🇹🇷" },
    { code: "de", name: "Deutsch", flag: "🇩🇪" },
    { code: "fr", name: "Français", flag: "🇫🇷" },
    { code: "es", name: "Español", flag: "🇪🇸" },
    { code: "it", name: "Italiano", flag: "🇮🇹" },
    { code: "pt", name: "Português", flag: "🇵🇹" },
    { code: "ru", name: "Русский", flag: "🇷🇺" },
    { code: "ar", name: "العربية", flag: "🇸🇦" },
    { code: "ja", name: "日本語", flag: "🇯🇵" },
    { code: "ko", name: "한국어", flag: "🇰🇷" },
    { code: "zh", name: "中文", flag: "🇨🇳" },
  ];
  const providerModelOptions: Record<string, string[]> = {
    openai: [
      "gpt-3.5-turbo",
      "gpt-4-turbo",
      "gpt-4o-mini",
      "gpt-4o",
      "gpt-4.1-mini",
      "gpt-4.1",
    ],
    claude: ["claude-3-5-sonnet", "claude-3-5-haiku", "claude-3-opus"],
  };
  const [sources, setSources] = useState<any[]>([]);
  const [newSourceTitle, setNewSourceTitle] = useState("");
  const [newSourceLang, setNewSourceLang] = useState("tr");
  const [selectedSourceId, setSelectedSourceId] = useState<number | null>(null);
  const [newText, setNewText] = useState("");
  const [fileUploading, setFileUploading] = useState(false);
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [contentType, setContentType] = useState<"text" | "file">("text");
  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceStatsMap, setSourceStatsMap] = useState<
    Record<number, { chunks: number; chars: number; tokensEst: number }>
  >({});
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [sourceDirty, setSourceDirty] = useState(false); // reserved for future unsaved-state indicator
  const [jobs, setJobs] = useState<any[]>([]);
  const [polling, setPolling] = useState(false);
  const [showDeleteToast, setShowDeleteToast] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<any | null>(null);
  const [trainingSubTab, setTrainingSubTab] = useState<
    "profile" | "retrieval" | "sources" | "search"
  >("profile");

  // Kullanıcı rollerini kontrol et
  const userRoles = user?.roles || [];
  const isMarketingSpecialist = userRoles.includes("marketing_specialist");
  const isCompanyOwner = userRoles.includes("company_owner");
  // const isAgencyOwner = userRoles.includes('agency_owner');

  const [formData, setFormData] = useState({
    name: chatbot?.name || "",
    description: chatbot?.description || "",
    initial_message:
      chatbot?.initial_message ||
      t("initial_message_default") ||
      "Hi there! How can I assist you today?",
    openai_api_key: chatbot?.openai_api_key || "",
    claude_api_key: (chatbot as any)?.claude_api_key || "",
    model: chatbot?.model || "gpt-3.5-turbo",
    // assistant_definition deprecated; use settings.assistant_profile.persona
    general_learning_set: chatbot?.general_learning_set || "",
    db_metadata_endpoint: chatbot?.db_metadata_endpoint || "",
    db_query_endpoint: chatbot?.db_query_endpoint || "",
    allowed_domains: chatbot?.allowed_domains?.join("\n") || "",
    company_id: chatbot?.company_id || defaultCompanyId || "",
    widget_settings:
      typeof chatbot?.widget_settings === "string"
        ? chatbot.widget_settings
        : chatbot?.widget_settings
          ? JSON.stringify(chatbot.widget_settings)
          : "{}",
    status: chatbot?.status || "draft",
    is_active: chatbot?.is_active ?? true,
    settings: chatbot?.settings || undefined,
    training_status: chatbot?.training_status || undefined,
    chat_mode: chatbot?.chat_mode || undefined,
    module: (chatbot as any)?.module || defaultModule || undefined,
  });

  // Keep company preselected when defaultCompanyId becomes available (e.g., after async fetch)
  useEffect(() => {
    if (!isEditMode && defaultCompanyId && formData.company_id !== defaultCompanyId) {
      setFormData((prev) => ({ ...prev, company_id: Number(defaultCompanyId) }));
    }
    // formData.company_id is intentionally not added to avoid endless loops; condition guards updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCompanyId, isEditMode]);

  const [companiesOptions, setCompaniesOptions] = useState<any[]>(companies);

  // Şirketleri yükle (company_owner için global çağrıyı atla)
  useEffect(() => {
    if (isCompanyOwner) return;
    if (companies.length === 0) {
      dispatch(getCompanies({}));
    }
  }, [dispatch, companies.length, isCompanyOwner]);

  // Sync local options when global companies change
  useEffect(() => {
    if (isCompanyOwner) {
      // company_owner: sadece kendi single şirketi
      companiesService.getMyCompany()
        .then((mc) => {
          if (mc && mc.id) {
            setCompaniesOptions([{ id: mc.id, company_name: mc.company_name, company_type: mc.company_type }]);
            if (!formData.company_id || Number(formData.company_id) !== Number(mc.id)) {
              setFormData((prev) => ({ ...prev, company_id: Number(mc.id) }));
            }
          }
        })
        .catch(() => {
          setCompaniesOptions([]);
        });
    } else {
      // diğer roller: global liste
      // benzersiz company id'lerine göre dizi oluştur
      const seen = new Set<number>();
      const unique = companies.filter((c: any) => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      setCompaniesOptions(unique);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, isCompanyOwner]);

  // Ensure defaultCompanyId is present in options even if not in fetched list
  useEffect(() => {
    if (isCompanyOwner) return; // company_owner için zaten tek şirket ekleniyor
    const ensureCompanyOption = async () => {
      if (!defaultCompanyId) return;
      const exists = companiesOptions?.some((c: any) => c?.id === Number(defaultCompanyId));
      if (!exists) {
        try {
          const c = await companiesService.getCompany(Number(defaultCompanyId));
          if (c && c.id) {
            setCompaniesOptions((prev) => {
              const has = prev?.some((p: any) => p.id === c.id);
              return has ? prev : [...prev, c];
            });
            return;
          }
        } catch {}
        try {
          const mc = await companiesService.getMyCompany();
          if (mc && mc.id === Number(defaultCompanyId)) {
            setCompaniesOptions((prev) => {
              const has = prev?.some((p: any) => p.id === mc.id);
              return has ? prev : [...prev, mc];
            });
          }
        } catch {}
      }
    };
    ensureCompanyOption();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultCompanyId, companiesOptions]);

  // Dışarıdan başlangıç sekmesini güncelle
  useEffect(() => {
    if (initialActiveTab) {
      setActiveTab(initialActiveTab);
    }
  }, [initialActiveTab]);

  // Load training data when modal opens in edit mode
  useEffect(() => {
    const loadTraining = async () => {
      if (!chatbot?.id) return;
      setTrainingLoading(true);
      setTrainingError(null);
      try {
        const s = await getChatbotSettings(chatbot.id);
        if (s && Object.keys(s).length > 0) {
          setSettingsData((prev: any) => ({ ...prev, ...s }));
          setDirtySettings(false);
        }
        const srcs = await listKnowledgeSources(chatbot.id);
        setSources(srcs);
        // prefetch basic stats per source using chunks list
        const statsEntries: [
          number,
          { chunks: number; chars: number; tokensEst: number },
        ][] = [];
        for (const src of srcs) {
          try {
            const chunks = await listSourceChunks(chatbot.id, src.id, {
              limit: 200,
            });
            const chunksCount = chunks.length;
            const totalChars = chunks.reduce(
              (acc: number, c: any) => acc + (c.len || 0),
              0
            );
            const tokensEst = Math.round(totalChars / 4);
            statsEntries.push([
              src.id,
              { chunks: chunksCount, chars: totalChars, tokensEst },
            ]);
          } catch {}
        }
        if (statsEntries.length) {
          setSourceStatsMap(Object.fromEntries(statsEntries));
        }
      } catch (e: any) {
        setTrainingError(e?.message || "Failed to load training data");
      } finally {
        setTrainingLoading(false);
      }
    };
    if (isEditMode) {
      loadTraining();
    }
  }, [isEditMode, chatbot?.id]);

  useEffect(() => {
    let timer: any;
    const poll = async () => {
      if (!chatbot?.id || !selectedSourceId) return;
      try {
        const list = await listSourceJobs(chatbot.id, selectedSourceId, {
          limit: 5,
        });
        setJobs(list);
        const hasRunning = list.some(
          (j: any) => j.status === "queued" || j.status === "running"
        );
        if (hasRunning) {
          timer = setTimeout(poll, 1500);
          setPolling(true);
        } else {
          setPolling(false);
        }
      } catch {}
    };
    poll();
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [chatbot?.id, selectedSourceId]);

  const handleSaveTrainingSettings = async () => {
    if (!chatbot?.id) return;
    setSavingSettings(true);
    setTrainingError(null);
    try {
      await updateChatbotSettings(chatbot.id, settingsData);
      setDirtySettings(false);
      setSaveSuccessAt(Date.now());
    } catch (e: any) {
      setTrainingError(e?.message || "Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleCreateSource = async () => {
    if (!chatbot?.id || !newSourceTitle.trim()) return null as any;
    const created = await createKnowledgeSource(chatbot.id, {
      type: contentType === "file" ? "file" : "text",
      title: newSourceTitle.trim(),
      lang: newSourceLang,
    });
    setSources([created, ...sources]);
    setNewSourceTitle("");
    setSelectedSourceId(created.id);
    return created;
  };

  const handleSaveSource = async () => {
    if (!chatbot?.id || !selectedSourceId) return;
    const body: any = {
      title: newSourceTitle?.trim?.() || "",
      lang: newSourceLang,
    };
    const updated = await updateKnowledgeSource(
      chatbot.id,
      selectedSourceId,
      body
    );
    setSources((prev) =>
      prev.map((p) => (p.id === selectedSourceId ? updated : p))
    );
  };

  const handleIngestText = async (overrideSourceId?: number) => {
    if (!chatbot?.id) return;
    const sid = overrideSourceId ?? selectedSourceId;
    if (!sid || !newText.trim()) return;
    await ingestTextToSource(chatbot.id, sid, newText);
    setNewText("");
  };

  // file upload handled inline in Sources tab onChange handler

  const handleSearchPreview = async () => {
    if (!chatbot?.id || !searchQ.trim()) {
      setSearchResults([]);
      return;
    }
    const res = await searchPreview(chatbot.id, searchQ.trim());
    setSearchResults(res);
  };

  // Form validation
  const validateForm = () => {
    const errors: string[] = [];

    if (!formData.name.trim()) {
      errors.push("Name is required");
    }

    if (!formData.company_id) {
      errors.push("Company selection is required");
    }

    // API key requirement by provider (skip for marketing specialist)
    const providerName = settingsData.provider?.name || "openai";
    if (!isMarketingSpecialist) {
      if (providerName === "openai" && !formData.openai_api_key.trim()) {
      errors.push("OpenAI API Key is required");
      }
      if (
        providerName === "claude" &&
        !(formData as any).claude_api_key?.trim()
      ) {
        errors.push("Claude API Key is required");
      }
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "checkbox") {
      const target = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: target.checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }

    // Clear validation errors when user starts typing
    if (validationErrors.length > 0) {
      setValidationErrors([]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Form verilerini hazırla
    let parsedWidgetSettings;
    try {
      // widget_settings bir string mi yoksa zaten bir obje mi kontrol et
      if (typeof formData.widget_settings === "string") {
        parsedWidgetSettings = JSON.parse(formData.widget_settings || "{}");
      } else {
        // Zaten bir obje ise, doğrudan kullan
        parsedWidgetSettings = formData.widget_settings;
      }
    } catch (error) {
      console.error("JSON parse error:", error);
      // Parse hatası durumunda boş obje kullan
      parsedWidgetSettings = {};
      setValidationErrors(["Widget settings is not a valid JSON format"]);
      setIsSubmitting(false);
      return;
    }

    const submissionData: any = {
      ...formData,
      allowed_domains: formData.allowed_domains
        ? formData.allowed_domains
            .split("\n")
            .filter((domain: string) => domain.trim())
        : [],
      company_id: Number(formData.company_id),
      is_active: Boolean(formData.is_active),
      widget_settings: parsedWidgetSettings,
    };

    // Ensure API keys align with backend schemas: do not send unsupported fields
    const providerName = settingsData.provider?.name || "openai";
    if (providerName === "openai") {
      // Backend supports openai_api_key, but not claude_api_key
      delete submissionData.claude_api_key;
    } else {
      // Backend does not accept claude_api_key in this endpoint, and openai key should not be updated for Claude
      delete submissionData.claude_api_key;
      delete submissionData.openai_api_key;
    }

    // Ensure training settings are included when saving from modal
    if (isEditMode) {
      submissionData.settings = settingsData;
    }

    try {
      if (isEditMode && chatbot) {
        await dispatch(
          updateChatbot({
            id: chatbot.id,
            chatbotData: submissionData,
          })
        ).unwrap();
      } else {
        await dispatch(createChatbot(submissionData)).unwrap();
      }

      if (onSuccess) onSuccess();
      onHide();
    } catch (error) {
      console.error("Error submitting form:", error);
      setValidationErrors(["An error occurred while saving the chatbot"]);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Icons are inlined in tab items

  const getStatusBadgeVariant = (status: string) => {
    const variants = {
      draft: "secondary",
      active: "success",
      paused: "warning",
      archived: "danger",
    };
    return variants[status as keyof typeof variants] || "secondary";
  };

  const getModelInfo = (model: string) => {
    const modelInfo = {
      "gpt-3.5-turbo": { name: "GPT-3.5 Turbo", cost: "Low", speed: "Fast" },
      "gpt-4-turbo": { name: "GPT-4 Turbo", cost: "Medium", speed: "Medium" },
      "gpt-4o": { name: "GPT-4o", cost: "High", speed: "Medium" },
      "gpt-4o-mini": { name: "GPT-4o Mini", cost: "Medium", speed: "Fast" },
    };
    return (
      modelInfo[model as keyof typeof modelInfo] || modelInfo["gpt-3.5-turbo"]
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      className="chatbot-form-modal"
    >
      <Modal.Header className="chatbot-form-header">
        <div className="d-flex align-items-center gap-3">
          <div className="modal-icon">
            <Icon icon={FaRobot} />
          </div>
          <div>
            <Modal.Title className="modal-title">
              {isEditMode ? (
                <>
                  {t("Edit Chatbot")}
                  <Badge bg="info" className="ms-2">
                    {chatbot.name}
                  </Badge>
                </>
              ) : (
                t("Create New Chatbot")
              )}
            </Modal.Title>
            <small className="modal-subtitle">
              {isEditMode
                ? t("modify_your_ai_assistant_settings")
                : t("configure_your_new_ai_assistant")}
            </small>
          </div>
        </div>
        <Button
          variant="outline-light"
          size="sm"
          onClick={onHide}
          className="close-btn"
        >
          ×
        </Button>
      </Modal.Header>

      <Modal.Body className="chatbot-form-body">
        {validationErrors.length > 0 && (
          <Alert variant="danger" className="validation-alert">
            <Icon icon={FaExclamationTriangle} className="me-2" />
            <strong>Please fix the following errors:</strong>
            <ul className="mb-0 mt-2">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || "general")}
        >
          <Nav variant="pills" className="custom-nav-tabs modal-tabbar">
            {[
              { key: "general", label: t("general"), icon: FaRobot },
              { key: "ai-config", label: t("ai_config"), icon: FaCog },
              { key: "training", label: "Training", icon: FaBrain },
              {
                key: "database-config",
                label: t("database_config"),
                icon: FaDatabase,
              },
              {
                key: "widget-settings",
                label: t("widget_settings"),
                icon: FaPalette,
              },
            ].map((tab) => (
              <Nav.Item key={tab.key}>
                <Nav.Link
                  eventKey={tab.key}
                  className="custom-nav-link modal-tablink"
                >
                  <Icon icon={tab.icon} className="me-2" />
                  {t(tab.label)}
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>

          <Tab.Content className="tab-content-custom">
            {/* General Tab */}
            <Tab.Pane eventKey="general">
              <div className="tab-pane-content">
                <div className="section-header">
                  <Icon icon={FaRobot} className="section-icon" />
                  <h5>{t("basic_information")}</h5>
                </div>

                <Form>
                  {/* Provider/Model selection moved to AI Configuration tab */}
                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-custom">
                          <Icon icon={FaUser} className="me-2" />
                          {t("chatbot_name")} *
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder={t("enter_chatbot_name")}
                          className="form-control-custom"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-custom">
                          <Icon icon={FaBuilding} className="me-2" />
                          {t("company")} *
                        </Form.Label>
                        <Form.Select
                          name="company_id"
                          value={formData.company_id}
                          onChange={handleInputChange}
                          disabled={isCompanyOwner || !!defaultCompanyId}
                          className="form-control-custom"
                          required
                        >
                          <option value="">{t("Select a company")}</option>
                          {companiesOptions.map((company) => (
                            <option key={company.id} value={company.id}>
                              {company.company_name}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="form-group-custom">
                    <Form.Label className="form-label-custom">
                      <Icon icon={FaInfoCircle} className="me-2" />
                      {t("description")}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      placeholder={t("describe_what_this_chatbot_does")}
                      className="form-control-custom"
                    />
                  </Form.Group>

                  <Form.Group className="form-group-custom">
                    <Form.Label className="form-label-custom">
                      <Icon icon={FaRobot} className="me-2" />
                      {t("initial_message")}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      name="initial_message"
                      value={formData.initial_message}
                      onChange={handleInputChange}
                      placeholder={t("first_message_users_will_see")}
                      className="form-control-custom"
                    />
                  </Form.Group>

                  <Form.Group className="form-group-custom">
                    <Form.Label className="form-label-custom">
                      <Icon icon={FaGlobe} className="me-2" />
                      {t("allowed_domains")}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      name="allowed_domains"
                      value={formData.allowed_domains}
                      onChange={handleInputChange}
                      placeholder={
                        "https://example.com\nhttps://another-domain.com"
                      }
                      className="form-control-custom"
                    />
                    <Form.Text className="form-text-custom">
                      <Icon icon={FaInfoCircle} className="me-1" />
                      {t(
                        "Leave empty to allow all domains. Enter one domain per line."
                      )}
                    </Form.Text>
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-custom">
                          {t("status")}
                        </Form.Label>
                        <Form.Select
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          className="form-control-custom"
                        >
                          <option value="draft">{t("Draft")}</option>
                          <option value="active">{t("Active")}</option>
                          <option value="paused">{t("Paused")}</option>
                          <option value="archived">{t("Archived")}</option>
                        </Form.Select>
                        <div className="mt-2">
                          <Badge bg={getStatusBadgeVariant(formData.status)}>
                            {formData.status.toUpperCase()}
                          </Badge>
                        </div>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-custom">
                          {t("Activation")}
                        </Form.Label>
                        <div className="custom-switch-container">
                          <Form.Check
                            type="switch"
                            id="is_active"
                            name="is_active"
                            checked={formData.is_active}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                is_active: e.target.checked,
                              })
                            }
                            className="custom-switch"
                          />
                          <div className="switch-label">
                            <Icon
                              icon={FaCheckCircle}
                              className={`status-icon ${formData.is_active ? "active" : "inactive"}`}
                            />
                            <span
                              className={
                                formData.is_active
                                  ? "text-success"
                                  : "text-muted"
                              }
                            >
                              {formData.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                        </div>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </div>
            </Tab.Pane>

            {/* AI Config Tab */}
            <Tab.Pane eventKey="ai-config">
              <div className="tab-pane-content">
                <div className="section-header">
                  <Icon icon={FaCog} className="section-icon" />
                  <h5>AI Configuration</h5>
                </div>

                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-custom">
                          Provider
                        </Form.Label>
                        <Form.Select
                          value={settingsData.provider?.name || "openai"}
                          onChange={(e) => {
                            const name = e.target.value;
                            const models = providerModelOptions[name] || [];
                            const nextModel = models[0] || "";
                            setSettingsData({
                              ...settingsData,
                              provider: { name, model: nextModel },
                            });
                            setDirtySettings(true);
                            // Keep top-level model in sync for backend compatibility
                            setFormData({ ...formData, model: nextModel });
                          }}
                          className="form-control-custom"
                        >
                          <option value="openai">OpenAI</option>
                          <option value="claude">Claude</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="form-group-custom">
                        <Form.Label className="form-label-custom">
                          Model
                        </Form.Label>
                        <Form.Select
                          value={settingsData.provider?.model || ""}
                          onChange={(e) => {
                            const model = e.target.value;
                            setSettingsData({
                              ...settingsData,
                              provider: { ...settingsData.provider, model },
                            });
                            setDirtySettings(true);
                            // Keep top-level model in sync
                            setFormData({ ...formData, model });
                          }}
                          className="form-control-custom"
                        >
                          {(
                            providerModelOptions[
                              settingsData.provider?.name || "openai"
                            ] || []
                          ).map((m) => (
                            <option key={m} value={m}>
                              {m}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="form-group-custom">
                    <Form.Label className="form-label-custom">
                      <Icon icon={FaKey} className="me-2" />
                      {(settingsData.provider?.name || "openai") === "openai"
                        ? t("OpenAI API Key")
                        : "Claude API Key"}{" "}
                      *
                      {isMarketingSpecialist && (
                        <Badge bg="warning" className="ms-2">
                          Read Only
                        </Badge>
                      )}
                    </Form.Label>
                    <Form.Control
                      type="password"
                      name={
                        (settingsData.provider?.name || "openai") === "openai"
                          ? "openai_api_key"
                          : "claude_api_key"
                      }
                      value={
                        (settingsData.provider?.name || "openai") === "openai"
                          ? formData.openai_api_key
                          : (formData as any).claude_api_key
                      }
                      onChange={handleInputChange}
                      placeholder={
                        (settingsData.provider?.name || "openai") === "openai"
                          ? "sk-..."
                          : "claude-key-..."
                      }
                      className="form-control-custom"
                      readOnly={isMarketingSpecialist}
                    />
                    <Form.Text className="form-text-custom">
                      <Icon icon={FaInfoCircle} className="me-1" />
                      {isMarketingSpecialist 
                        ? "This field can only be modified by agency owners or administrators."
                        : (settingsData.provider?.name || "openai") === "openai"
                          ? t(
                              "Your OpenAI API key will be securely encrypted and stored"
                            )
                          : "Your Claude API key will be securely encrypted and stored"}
                    </Form.Text>
                  </Form.Group>
                  {(settingsData.provider?.name || "openai") === "openai" && (
                    <div className="model-info">
                      <div className="model-badges">
                        <Badge bg="primary">
                          {getModelInfo(formData.model).name}
                        </Badge>
                        <Badge bg="info">
                          Cost: {getModelInfo(formData.model).cost}
                        </Badge>
                        <Badge bg="success">
                          Speed: {getModelInfo(formData.model).speed}
                        </Badge>
                      </div>
                    </div>
                  )}

                  {/* Assistant Personality removed per request */}

                  <Card className="glass-card mt-3">
                    <Card.Header className="gradient-card-header">
                      Retrieval & Limits
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Top K</Form.Label>
                    <Form.Control
                              type="number"
                              value={settingsData.retrieval?.top_k || 3}
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  retrieval: {
                                    ...settingsData.retrieval,
                                    top_k: Number(e.target.value),
                                  },
                                });
                                setDirtySettings(true);
                              }}
                            />
                  </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Snippet Chars</Form.Label>
                            <Form.Control
                              type="number"
                              value={
                                settingsData.retrieval?.snippet_chars || 400
                              }
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  retrieval: {
                                    ...settingsData.retrieval,
                                    snippet_chars: Number(e.target.value),
                                  },
                                });
                                setDirtySettings(true);
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Chunk Size</Form.Label>
                            <Form.Control
                              type="number"
                              value={settingsData.retrieval?.chunk_size || 500}
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  retrieval: {
                                    ...settingsData.retrieval,
                                    chunk_size: Number(e.target.value),
                                  },
                                });
                                setDirtySettings(true);
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Chunk Overlap</Form.Label>
                            <Form.Control
                              type="number"
                              value={
                                settingsData.retrieval?.chunk_overlap || 60
                              }
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  retrieval: {
                                    ...settingsData.retrieval,
                                    chunk_overlap: Number(e.target.value),
                                  },
                                });
                                setDirtySettings(true);
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <Row>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Max Output Tokens</Form.Label>
                            <Form.Control
                              type="number"
                              value={
                                settingsData.limits?.max_output_tokens || 300
                              }
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  limits: {
                                    ...settingsData.limits,
                                    max_output_tokens: Number(e.target.value),
                                  },
                                });
                                setDirtySettings(true);
                              }}
                            />
                          </Form.Group>
                        </Col>
                        <Col>
                          <Form.Group className="mb-2">
                            <Form.Label>Cache TTL (sec)</Form.Label>
                            <Form.Control
                              type="number"
                              value={
                                settingsData.limits?.cache_ttl_seconds || 3600
                              }
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  limits: {
                                    ...settingsData.limits,
                                    cache_ttl_seconds: Number(e.target.value),
                                  },
                                });
                                setDirtySettings(true);
                              }}
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <small className="text-muted">
                          Tweak retrieval and provider to balance cost and
                          quality.
                        </small>
                        <Button
                          disabled={
                            savingSettings || !isEditMode || !dirtySettings
                          }
                          onClick={handleSaveTrainingSettings}
                        >
                          <Icon icon={FaSave} className="me-1" />
                          {savingSettings ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </Card.Body>
                  </Card>
                </Form>
              </div>
            </Tab.Pane>

            {/* Training Tab */}
            <Tab.Pane eventKey="training">
              <div className="tab-pane-content">
                <div className="section-header">
                  <Icon icon={FaBrain} className="section-icon" />
                  <h5>Training</h5>
                </div>

                {trainingLoading && (
                  <div className="mb-3 text-muted">
                    Loading training data...
                  </div>
                )}
                {trainingError && (
                  <Alert variant="danger">{trainingError}</Alert>
                )}

                <Tab.Container
                  activeKey={trainingSubTab}
                  onSelect={(k) => setTrainingSubTab((k as any) || "profile")}
                >
                  <Nav variant="pills" className="modal-tabbar mb-3">
                    {[
                      {
                        key: "profile",
                        label: "Assistant Profile & Guardrails",
                      },
                      { key: "sources", label: "Sources" },
                      { key: "search", label: "Search Preview" },
                    ].map((tab) => (
                      <Nav.Item key={tab.key}>
                        <Nav.Link
                          eventKey={tab.key}
                          className="custom-nav-link modal-tablink"
                        >
                          {tab.label}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                  <Tab.Content>
                    <Tab.Pane eventKey="profile">
                      <Card className="glass-card mb-3">
                        <Card.Body>
                          <Form.Group className="mb-2">
                            <Form.Label>Persona</Form.Label>
                    <Form.Control
                      as="textarea"
                              rows={4}
                              value={
                                settingsData.assistant_profile?.persona ?? ""
                              }
                              onChange={(e) => {
                                setSettingsData({
                                  ...settingsData,
                                  assistant_profile: {
                                    ...(settingsData.assistant_profile || {}),
                                    persona: e.target.value,
                                  },
                                });
                                setDirtySettings(true);
                              }}
                              placeholder="Example: Warm, professional energy consultant who explains clearly and concisely..."
                            />
                          </Form.Group>
                          <Row>
                            <Col>
                              <Form.Group className="mb-2">
                                <Form.Label>Tone</Form.Label>
                                {(() => {
                                  const currentTone: string =
                                    settingsData.assistant_profile?.tone ||
                                    "friendly";
                                  const toneSelectVal = tonePresets.includes(
                                    currentTone
                                  )
                                    ? currentTone
                                    : "custom";
                                  return (
                                    <>
                                      <Form.Select
                                        value={toneSelectVal}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          if (val === "custom") {
                                            // keep current custom tone
                                          } else {
                                            setSettingsData({
                                              ...settingsData,
                                              assistant_profile: {
                                                ...(settingsData.assistant_profile ||
                                                  {}),
                                                tone: val,
                                              },
                                            });
                                          }
                                          setDirtySettings(true);
                                        }}
                                      >
                                        {tonePresets.map((t) => (
                                          <option key={t} value={t}>
                                            {t}
                                          </option>
                                        ))}
                                        <option value="custom">custom…</option>
                                      </Form.Select>
                                      {toneSelectVal === "custom" && (
                                        <Form.Control
                                          className="mt-2"
                                          value={currentTone}
                                          onChange={(e) => {
                                            setSettingsData({
                                              ...settingsData,
                                              assistant_profile: {
                                                ...(settingsData.assistant_profile ||
                                                  {}),
                                                tone: e.target.value,
                                              },
                                            });
                                            setDirtySettings(true);
                                          }}
                                          placeholder="Enter a custom tone (e.g., enthusiastic, analytical)"
                                        />
                                      )}
                                    </>
                                  );
                                })()}
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group className="mb-2">
                                <Form.Label>Default Language</Form.Label>
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                  <Form.Select
                                    value={
                                      settingsData.assistant_profile
                                        ?.default_language ?? "en"
                                    }
                                    onChange={(e) => {
                                      setSettingsData({
                                        ...settingsData,
                                        assistant_profile: {
                                          ...(settingsData.assistant_profile ||
                                            {}),
                                          default_language: e.target.value,
                                        },
                                      });
                                      setDirtySettings(true);
                                    }}
                                    style={{ maxWidth: 260 }}
                                  >
                                    {languages.map((l) => (
                                      <option
                                        key={l.code}
                                        value={l.code}
                                      >{`${l.flag} ${l.name} (${l.code})`}</option>
                                    ))}
                                  </Form.Select>
                                  <Form.Check
                                    type="checkbox"
                                    id="reply_in_user_language"
                                    label="Reply in user's language"
                                    checked={Boolean(
                                      settingsData.assistant_profile
                                        ?.reply_in_user_language
                                    )}
                                    onChange={(e) => {
                                      setSettingsData({
                                        ...settingsData,
                                        assistant_profile: {
                                          ...(settingsData.assistant_profile ||
                                            {}),
                                          reply_in_user_language:
                                            e.target.checked,
                                        },
                                      });
                                      setDirtySettings(true);
                                    }}
                                    className="reply-lang-check"
                                  />
                                </div>
                              </Form.Group>
                            </Col>
                          </Row>
                          <Row>
                            <Col>
                              <Form.Group className="mb-2">
                                <Form.Label>Answer Format</Form.Label>
                                <Form.Control
                                  as="textarea"
                                  rows={4}
                                  value={
                                    settingsData.assistant_profile
                                      ?.answer_format ?? "short_paragraphs"
                                  }
                                  onChange={(e) => {
                                    setSettingsData({
                                      ...settingsData,
                                      assistant_profile: {
                                        ...(settingsData.assistant_profile ||
                                          {}),
                                        answer_format: e.target.value,
                                      },
                                    });
                                    setDirtySettings(true);
                                  }}
                                  placeholder="short_paragraphs, bullets, numbered steps, …"
                                />
                              </Form.Group>
                            </Col>
                            <Col>
                              <Form.Group className="mb-2">
                                <Form.Label>Max Answer Tokens</Form.Label>
                                <Form.Control
                                  type="number"
                                  value={
                                    settingsData.assistant_profile
                                      ?.max_answer_tokens ?? 250
                                  }
                                  onChange={(e) => {
                                    setSettingsData({
                                      ...settingsData,
                                      assistant_profile: {
                                        ...(settingsData.assistant_profile ||
                                          {}),
                                        max_answer_tokens: Number(
                                          e.target.value
                                        ),
                                      },
                                    });
                                    setDirtySettings(true);
                                  }}
                                />
                              </Form.Group>
                            </Col>
                          </Row>
                          <Form.Group className="mb-2">
                            <Form.Label>Banned Topics</Form.Label>
                            <div className="d-flex flex-wrap gap-2 mb-3">
                              {(
                                settingsData.guardrails?.banned_topics || []
                              ).map((topic: string, idx: number) => (
                                <span
                                  key={idx}
                                  className="chip chip-selected d-flex align-items-center"
                                  style={{ gap: 6 }}
                                >
                                  {topic}
                                  <button
                                    type="button"
                                    className="btn btn-sm p-0 text-white ms-1"
                                    onClick={() => {
                                      const arr = (
                                        settingsData.guardrails
                                          ?.banned_topics || []
                                      ).filter(
                                        (_: string, i: number) => i !== idx
                                      );
                                      setSettingsData({
                                        ...settingsData,
                                        guardrails: {
                                          ...(settingsData.guardrails || {}),
                                          banned_topics: arr,
                                        },
                                      });
                                      setDirtySettings(true);
                                    }}
                                  >
                                    <Icon icon={FaTimes} />
                                  </button>
                                </span>
                              ))}
                            </div>
                            <div className="d-flex flex-wrap gap-2 mb-2">
                              {(
                                bannedSuggestionsByLang[
                                  settingsData.assistant_profile
                                    ?.default_language || "en"
                                ] || bannedSuggestionsByLang.en
                              )
                                .filter(
                                  (sug) =>
                                    !(
                                      settingsData.guardrails?.banned_topics ||
                                      []
                                    ).includes(sug)
                                )
                                .map((sug) => (
                                  <button
                                    key={sug}
                                    type="button"
                                    className="chip chip-suggest"
                                    onClick={() => {
                                      const arr = Array.from(
                                        new Set([
                                          ...(settingsData.guardrails
                                            ?.banned_topics || []),
                                          sug,
                                        ])
                                      );
                                      setSettingsData({
                                        ...settingsData,
                                        guardrails: {
                                          ...(settingsData.guardrails || {}),
                                          banned_topics: arr,
                                        },
                                      });
                                      setDirtySettings(true);
                                    }}
                                  >
                                    + {sug}
                                  </button>
                                ))}
                            </div>
                            <InputGroup>
                              <Form.Control
                                placeholder="Add a topic and press Enter"
                                onKeyDown={(e: any) => {
                                  if (e.key === "Enter") {
                                    e.preventDefault();
                                    const v = (e.target.value || "").trim();
                                    if (!v) return;
                                    const arr = [
                                      ...(settingsData.guardrails
                                        ?.banned_topics || []),
                                      v,
                                    ];
                                    setSettingsData({
                                      ...settingsData,
                                      guardrails: {
                                        ...(settingsData.guardrails || {}),
                                        banned_topics: arr,
                                      },
                                    });
                                    (e.target as any).value = "";
                                    setDirtySettings(true);
                                  }
                                }}
                              />
                              <Button
                                variant="outline-secondary"
                                onClick={() => {
                                  setSettingsData({
                                    ...settingsData,
                                    guardrails: {
                                      ...(settingsData.guardrails || {}),
                                      banned_topics: [],
                                    },
                                  });
                                  setDirtySettings(true);
                                }}
                              >
                                <Icon icon={FaUndo} className="me-1" />
                                Reset
                              </Button>
                            </InputGroup>
                          </Form.Group>
                          <div className="d-flex justify-content-between align-items-center mt-3">
                            <small className="text-muted">
                              Changes are not applied until saved.
                            </small>
                            <div
                              className="d-flex align-items-center"
                              style={{ gap: 8 }}
                            >
                              {saveSuccessAt && (
                                <span
                                  className="text-success d-flex align-items-center"
                                  style={{ gap: 6 }}
                                >
                                  <Icon icon={FaCheck} /> Saved
                                </span>
                              )}
                              <Button
                                disabled={
                                  savingSettings ||
                                  !isEditMode ||
                                  !dirtySettings
                                }
                                onClick={handleSaveTrainingSettings}
                              >
                                <Icon icon={FaSave} className="me-1" />
                                {savingSettings ? "Saving..." : "Save"}
                              </Button>
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>
                    <Tab.Pane eventKey="sources">
                      <Card className="glass-card mb-3">
                        <Card.Header className="gradient-card-header d-flex justify-content-between align-items-center">
                          <span>
                            {sourceDirty && (
                              <Badge bg="warning" className="ms-2">
                                Unsaved
                              </Badge>
                            )}
                          </span>
                          <Button
                            size="sm"
                            variant="outline-light"
                            onClick={() => {
                              setSelectedSourceId(null);
                              setNewSourceTitle("");
                              setNewSourceLang("tr");
                              setContentType("text");
                              setNewText("");
                              setPendingFile(null);
                              setSourceDirty(false);
                            }}
                          >
                            + Add New Source
                          </Button>
                        </Card.Header>
                        <Card.Body>
                          <Row className="g-3 align-items-end">
                            <Col md={6} lg={5}>
                              <Form.Group>
                                <Form.Label>Source Title</Form.Label>
                                <Form.Control
                                  placeholder="e.g., Company FAQs, Product Docs"
                                  value={newSourceTitle}
                                  onChange={(e) => {
                                    setNewSourceTitle(e.target.value);
                                    if (selectedSourceId) setSourceDirty(true);
                                  }}
                                />
                  </Form.Group>
                            </Col>
                            <Col md={3} lg={2}>
                              <Form.Group>
                                <Form.Label>Language</Form.Label>
                                <Form.Select
                                  value={newSourceLang}
                                  onChange={(e) => {
                                    setNewSourceLang(e.target.value);
                                    if (selectedSourceId) setSourceDirty(true);
                                  }}
                                >
                                  {(languages || []).map((l) => (
                                    <option
                                      key={l.code}
                                      value={l.code}
                                    >{`${l.flag} ${l.code}`}</option>
                                  ))}
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={3} lg={3}>
                              <Form.Group>
                                <Form.Label>Content Type</Form.Label>
                                <Form.Select
                                  value={contentType}
                                  onChange={(e) => {
                                    setContentType(
                                      e.target.value as "text" | "file"
                                    );
                                    if (selectedSourceId) setSourceDirty(true);
                                  }}
                                >
                                  <option value="text">Text</option>
                                  <option value="file">File Upload</option>
                                </Form.Select>
                              </Form.Group>
                            </Col>
                            <Col md={12}>
                              {contentType === "text" ? (
                                <Form.Group>
                                  <Form.Label>Text</Form.Label>
                                  <Form.Control
                                    as="textarea"
                                    rows={6}
                                    value={newText}
                                    onChange={(e) => setNewText(e.target.value)}
                                    placeholder={
                                      !selectedSourceId
                                        ? "You can enter text now. Click Create Source, then Ingest."
                                        : "Paste or type the content to ingest as a new version…"
                                    }
                                  />
                                </Form.Group>
                              ) : (
                                <Form.Group>
                                  <Form.Label>File</Form.Label>
                                  <Form.Control
                                    type="file"
                                    onChange={(
                                      e: React.ChangeEvent<HTMLInputElement>
                                    ) => {
                                      const f = e.target.files?.[0] || null;
                                      setPendingFile(f);
                                    }}
                                  />
                                  {pendingFile && (
                                    <small className="text-muted">
                                      Selected: {pendingFile.name}
                                    </small>
                                  )}
                                </Form.Group>
                              )}
                            </Col>
                            <Col md={12}>
                              <div className="d-flex flex-wrap align-items-center gap-2">
                                {!selectedSourceId ? (
                                  <Button
                                    disabled={
                                      !isEditMode ||
                                      !newSourceTitle.trim() ||
                                      (contentType === "text"
                                        ? !newText.trim()
                                        : !pendingFile)
                                    }
                                    onClick={async () => {
                                      const created =
                                        await handleCreateSource();
                                      if (!created) return;
                                      if (contentType === "text") {
                                        await handleIngestText(created.id);
                                      } else if (pendingFile && chatbot?.id) {
                                        setFileUploading(true);
                                        try {
                                          const up = await uploadSourceFile(
                                            chatbot.id,
                                            created.id,
                                            pendingFile
                                          );
                                          await ingestUploadedFile(
                                            chatbot.id,
                                            created.id,
                                            up.upload_id
                                          );
                                          setPendingFile(null);
                                        } finally {
                                          setFileUploading(false);
                                        }
                                      }
                                    }}
                                  >
                                    <Icon icon={FaPlus} className="me-1" />
                                    Create & Ingest
                                  </Button>
                                ) : (
                                  <Button
                                    variant="primary"
                                    disabled={
                                      !isEditMode ||
                                      !newSourceTitle.trim() ||
                                      (contentType === "text"
                                        ? !newText.trim()
                                        : !pendingFile)
                                    }
                                    onClick={async () => {
                                      await handleSaveSource();
                                      setSourceDirty(false);
                                      if (contentType === "text") {
                                        await handleIngestText();
                                      } else if (
                                        pendingFile &&
                                        chatbot?.id &&
                                        selectedSourceId
                                      ) {
                                        setFileUploading(true);
                                        try {
                                          const up = await uploadSourceFile(
                                            chatbot.id,
                                            selectedSourceId,
                                            pendingFile
                                          );
                                          await ingestUploadedFile(
                                            chatbot.id,
                                            selectedSourceId,
                                            up.upload_id
                                          );
                                          setPendingFile(null);
                                        } finally {
                                          setFileUploading(false);
                                        }
                                      }
                                    }}
                                  >
                                    <Icon icon={FaSave} className="me-1" />
                                    Save & Ingest as New Version
                                  </Button>
                                )}
                                {fileUploading && (
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="spinner-border spinner-border-sm"
                                      role="status"
                                    />
                                    <small>Uploading…</small>
                                  </div>
                                )}
                              </div>
                            </Col>
                            <Col md={12}>
                              <div className="d-flex justify-content-between align-items-center">
                                <small className="text-muted">Sources</small>
                                {selectedSourceId && (
                                  <small className="text-muted">
                                    Current: #{selectedSourceId}
                                  </small>
                                )}
                              </div>
                              <ListGroup
                                style={{ maxHeight: 300, overflowY: "auto" }}
                                className="mt-1"
                              >
                                {sources.map((s) => {
                                  const typeLabel =
                                    (s.type || "").toString().toUpperCase() ||
                                    "TEXT";
                                  const stats = sourceStatsMap[s.id];
                                  return (
                                    <ListGroup.Item
                                      key={s.id}
                                      active={s.id === selectedSourceId}
                                      className="source-item"
                                      onClick={async () => {
                                        setSelectedSourceId(s.id);
                                        setNewSourceTitle(s.title || "");
                                        setNewSourceLang(
                                          s.lang || newSourceLang
                                        );
                                        const isFile =
                                          (s.type || "")
                                            .toString()
                                            .toLowerCase() === "file";
                                        setContentType(
                                          isFile ? "file" : "text"
                                        );
                                        setSourceDirty(false);
                                        setPendingFile(null);
                                        if (!isFile && chatbot?.id) {
                                          try {
                                            const content =
                                              await getSourceContent(
                                                chatbot.id,
                                                s.id
                                              );
                                            setNewText(content.text || "");
                                          } catch {
                                            setNewText("");
                                          }
                                        } else {
                                          setNewText("");
                                        }
                                      }}
                                    >
                                      <div className="source-row">
                                        <div className="source-main">
                                          <Badge
                                            bg="secondary"
                                            className="source-type"
                                          >
                                            {typeLabel}
                                          </Badge>
                                          <div className="source-title">
                                            {s.title || "(untitled)"}
                                          </div>
                                          {!s.is_active && (
                                            <Badge
                                              bg="warning"
                                              className="ms-1"
                                            >
                                              Inactive
                                            </Badge>
                                          )}
                                        </div>
                                        <div className="source-meta">
                                          <span>#{s.id}</span>
                                          {s.version != null && (
                                            <span>v{s.version}</span>
                                          )}
                                          <span>{s.lang}</span>
                                          {stats?.chunks != null && (
                                            <span>{stats.chunks} chunks</span>
                                          )}
                                          {stats?.tokensEst != null && (
                                            <span>
                                              ~{stats.tokensEst} tokens
                                            </span>
                                          )}
                                          {s.updated_at && (
                                            <span>
                                              upd{" "}
                                              {new Date(
                                                s.updated_at
                                              ).toLocaleDateString()}
                                            </span>
                                          )}
                                        </div>
                                        <div
                                          className="source-actions"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <Form.Check
                                            type="switch"
                                            id={`src-active-${s.id}`}
                                            checked={!!s.is_active}
                                            disabled={sourceLoading}
                                            onChange={async (e) => {
                                              if (!chatbot?.id) return;
                                              setSourceLoading(true);
                                              try {
                                                const updated =
                                                  await updateKnowledgeSource(
                                                    chatbot.id,
                                                    s.id,
                                                    {
                                                      is_active:
                                                        e.target.checked,
                                                    }
                                                  );
                                                setSources((prev) =>
                                                  prev.map((p) =>
                                                    p.id === s.id ? updated : p
                                                  )
                                                );
                                              } finally {
                                                setSourceLoading(false);
                                              }
                                            }}
                                            label=""
                                          />
                                          <Button
                                            size="sm"
                                            variant="outline-danger"
                                            className="icon-btn"
                                            disabled={sourceLoading}
                                            onClick={() => {
                                              if (!chatbot?.id) return;
                                              setDeleteCandidate(s);
                                              setShowDeleteToast(true);
                                            }}
                                          >
                                            <Icon icon={FaTrash} />
                                          </Button>
                                        </div>
                                      </div>
                                    </ListGroup.Item>
                                  );
                                })}
                                {sources.length === 0 && (
                                  <ListGroup.Item className="text-muted">
                                    No sources yet
                                  </ListGroup.Item>
                                )}
                              </ListGroup>
                              {selectedSourceId && (
                                <div
                                  className="mt-2 small text-muted d-flex align-items-center"
                                  style={{ gap: 10 }}
                                >
                                  <span>Recent jobs:</span>
                                  {jobs.length === 0 ? (
                                    <span>none</span>
                                  ) : (
                                    jobs.slice(0, 3).map((j: any) => (
                                      <Badge
                                        key={j.id}
                                        bg={
                                          j.status === "succeeded"
                                            ? "success"
                                            : j.status === "failed"
                                              ? "danger"
                                              : "warning"
                                        }
                                      >
                                        #{j.id} {j.status}
                                        {j.version ? ` v${j.version}` : ""}
                                      </Badge>
                                    ))
                                  )}
                                  {polling && (
                                    <span className="ms-2">(updating...)</span>
                                  )}
                                </div>
                              )}
                            </Col>
                          </Row>
                        </Card.Body>
                      </Card>
                    </Tab.Pane>
                    <Tab.Pane eventKey="search">
                      <Card className="mb-2">
                        <Card.Header>Search Preview</Card.Header>
                        <Card.Body>
                          <InputGroup className="mb-2">
                            <Form.Control
                              placeholder="Your question or keywords"
                              value={searchQ}
                              onChange={(e) => setSearchQ(e.target.value)}
                            />
                            <Button
                              disabled={!isEditMode}
                              onClick={handleSearchPreview}
                            >
                              Search
                            </Button>
                          </InputGroup>
                          {searchResults.length === 0 ? (
                            <div className="text-muted">No results</div>
                          ) : (
                            <ListGroup>
                              {searchResults.map((r, idx) => {
                                const terms = (searchQ || "")
                                  .split(/\s+/)
                                  .filter(Boolean);
                                let snippet = r.snippet as string;
                                terms.forEach((t: string) => {
                                  try {
                                    snippet = snippet.replace(
                                      new RegExp(
                                        `(${t.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")})`,
                                        "gi"
                                      ),
                                      "<mark>$1</mark>"
                                    );
                                  } catch {}
                                });
                                return (
                                  <ListGroup.Item key={idx}>
                                    <div className="d-flex justify-content-between">
                                      <strong>Score:</strong>{" "}
                                      <span>{r.score}</span>
                                    </div>
                                    <div
                                      style={{
                                        whiteSpace: "pre-wrap",
                                        margin: 0,
                                      }}
                                      dangerouslySetInnerHTML={{
                                        __html: snippet,
                                      }}
                                    />
                                  </ListGroup.Item>
                                );
                              })}
                            </ListGroup>
                          )}
                        </Card.Body>
                      </Card>
                    </Tab.Pane>
                  </Tab.Content>
                </Tab.Container>
              </div>
            </Tab.Pane>

            {/* Database Config Tab */}
            <Tab.Pane eventKey="database-config">
              <div className="tab-pane-content">
                <div className="section-header">
                  <Icon icon={FaDatabase} className="section-icon" />
                  <h5>{t("database_integration")}</h5>
                </div>

                <Form>
                  <Form.Group className="form-group-custom">
                    <Form.Label className="form-label-custom">
                      <Icon icon={FaDatabase} className="me-2" />
                      {t("database_metadata_endpoint")}
                    </Form.Label>
                    <Form.Control
                      type="url"
                      name="db_metadata_endpoint"
                      value={formData.db_metadata_endpoint}
                      onChange={handleInputChange}
                      placeholder="https://api.example.com/metadata"
                      className="form-control-custom"
                    />
                    <Form.Text className="form-text-custom">
                      <Icon icon={FaInfoCircle} className="me-1" />
                      {t(
                        "url_endpoint_for_retrieving_database_schema_metadata"
                      )}
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="form-group-custom">
                    <Form.Label className="form-label-custom">
                      <Icon icon={FaDatabase} className="me-2" />
                      {t("database_query_endpoint")}
                    </Form.Label>
                    <Form.Control
                      type="url"
                      name="db_query_endpoint"
                      value={formData.db_query_endpoint}
                      onChange={handleInputChange}
                      placeholder="https://api.example.com/query"
                      className="form-control-custom"
                    />
                    <Form.Text className="form-text-custom">
                      <Icon icon={FaInfoCircle} className="me-1" />
                      {t("url_endpoint_for_executing_database_queries")}
                    </Form.Text>
                  </Form.Group>
                </Form>
              </div>
            </Tab.Pane>

            {/* Widget Settings Tab */}
            <Tab.Pane eventKey="widget-settings">
              <div className="tab-pane-content">
                <div className="section-header">
                  <Icon icon={FaPalette} className="section-icon" />
                  <h5>{t("widget_customization")}</h5>
                </div>

                <WidgetCustomizationBuilder
                  value={
                    typeof formData.widget_settings === "string"
                      ? (() => {
                          try {
                            return JSON.parse(formData.widget_settings || "{}");
                          } catch {
                            return {};
                          }
                        })()
                      : formData.widget_settings
                  }
                  onChange={(nextObj: any) =>
                    setFormData({
                      ...formData,
                      widget_settings: JSON.stringify(nextObj),
                    })
                  }
                />
              </div>
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Modal.Body>

      <Modal.Footer className="chatbot-form-footer">
        <div className="footer-actions">
          <Button
            variant="outline-secondary"
            onClick={onHide}
            className="cancel-btn"
            disabled={isSubmitting}
          >
            {t("Cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            className="submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <div
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                >
                  <span className="visually-hidden">{t("loading")}</span>
                </div>
                {isEditMode ? t("updating") : t("creating")}
              </>
            ) : (
              <>
                <Icon icon={isEditMode ? FaCog : FaRobot} className="me-2" />
                {isEditMode ? t("update_chatbot") : t("create_chatbot")}
              </>
            )}
          </Button>
        </div>
      </Modal.Footer>

      {/* Delete confirm toast */}
      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          onClose={() => setShowDeleteToast(false)}
          show={showDeleteToast}
          delay={8000}
          autohide
          bg="light"
        >
          <Toast.Header closeButton={true}>
            <strong className="me-auto">Confirm Delete</strong>
            {deleteCandidate && <small>#{deleteCandidate.id}</small>}
          </Toast.Header>
          <Toast.Body>
            <div className="d-flex flex-column" style={{ gap: 10 }}>
              <div>Delete source {deleteCandidate?.title || "(untitled)"}?</div>
              <div className="d-flex" style={{ gap: 8 }}>
                <Button
                  size="sm"
                  variant="danger"
                  onClick={async () => {
                    if (!chatbot?.id || !deleteCandidate) return;
                    const id = deleteCandidate.id;
                    await deleteKnowledgeSource(chatbot.id, id);
                    setSources((prev) => prev.filter((p) => p.id !== id));
                    if (selectedSourceId === id) setSelectedSourceId(null);
                    setShowDeleteToast(false);
                    setDeleteCandidate(null);
                  }}
                >
                  Delete
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setShowDeleteToast(false);
                    setDeleteCandidate(null);
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <style>{`
        .glass-card {
          background: rgba(255,255,255,0.78) !important;
          backdrop-filter: blur(10px);
          border: 1px solid rgba(226, 232, 240, 0.7) !important;
          box-shadow: 0 12px 38px rgba(2, 6, 23, 0.08) !important;
        }
        .gradient-card-header {
          background: linear-gradient(135deg, #0ea5e9, #6366f1);
          color: #f8fafc;
          font-weight: 700;
        }
        .chip {
          border: 1px solid #e2e8f0;
          border-radius: 999px;
          padding: 6px 12px;
          font-size: 12px;
          background: #f1f5f9;
          color: #0f172a;
        }
        .chip-suggest:hover { background: #eef2ff; border-color: #c7d2fe; }
        .chip-disabled { opacity: .6; cursor: not-allowed; }
        .chip-selected { background: #111827; color: #fff; border-color: #111827; }

        /* Ensure labels are readable on glass background */
        .glass-card label,
        .glass-card .form-label,
        .glass-card .form-label-custom {
          color: #0f172a !important;
        }
        [data-bs-theme="dark"] .glass-card label,
        [data-bs-theme="dark"] .glass-card .form-label,
        [data-bs-theme="dark"] .glass-card .form-label-custom {
          color: #e2e8f0 !important;
        }

        /* Input group text contrast */
        .glass-card .input-group-text {
          background: #eef2f7 !important;
          color: #0f172a !important;
          border-color: rgba(15, 23, 42, 0.08) !important;
        }
        [data-bs-theme="dark"] .glass-card .input-group-text {
          background: #374151 !important;
          color: #f8fafc !important;
          border-color: #475569 !important;
        }

        .modal-tabbar {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          overflow-x: auto;
          scrollbar-width: thin;
          padding-bottom: 6px;
        }
        .modal-tablink {
          padding: 10px 14px !important;
          font-size: 13px !important;
          white-space: nowrap;
        }
        @media (max-width: 1200px) {
          .chatbot-form-modal .modal-dialog { max-width: 98%; }
          .modal-tablink { padding: 8px 12px !important; font-size: 12px !important; }
        }
        @media (max-width: 768px) {
          .modal-tabbar { gap: 4px; }
          .modal-tablink { padding: 6px 10px !important; font-size: 12px !important; }
        }
        .chatbot-form-modal .modal-dialog {
          max-width: 1100px;
        }

        .chatbot-form-modal .modal-content {
          border: none;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 22px 70px rgba(2, 6, 23, 0.18);
        }

        .chatbot-form-header {
          background: linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%);
          color: #f8fafc;
          padding: 24px 32px;
          border: none;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .modal-icon {
          width: 56px;
          height: 56px;
          background: rgba(255, 255, 255, 0.14);
          border-radius: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: #f8fafc;
          backdrop-filter: blur(10px);
        }

        .modal-title {
          color: white;
          margin: 0;
          font-size: 24px;
          font-weight: 700;
          line-height: 1.2;
        }

        .modal-subtitle {
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        .close-btn {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          font-weight: bold;
          border: 1px solid rgba(255, 255, 255, 0.3);
        }

        .close-btn:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.5);
        }

        .chatbot-form-body {
          padding: 32px;
          background: #f8fafc;
          max-height: 70vh;
          overflow-y: auto;
        }

        .validation-alert {
          border-radius: 12px;
          border: none;
          margin-bottom: 24px;
        }

        .custom-nav-tabs {
          background: white;
          border-radius: 16px;
          padding: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 24px;
          border: 1px solid #e2e8f0;
        }

        .custom-nav-link {
          border: none !important;
          border-radius: 12px !important;
          padding: 12px 20px !important;
          color: #64748b !important;
          font-weight: 600 !important;
          transition: all 0.2s ease !important;
          display: flex;
          align-items: center;
          margin: 0 4px;
        }

        .custom-nav-link:hover {
          background: #f1f5f9 !important;
          color: #475569 !important;
        }

        .custom-nav-link.active {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
          color: white !important;
          box-shadow: 0 4px 12px rgba(102, 126, 234, 0.25);
        }

        .tab-content-custom {
          background: #ffffff;
          border-radius: 16px;
          padding: 32px;
          box-shadow: 0 8px 24px rgba(2, 6, 23, 0.06);
          border: 1px solid #e5e7eb;
        }

        .tab-pane-content {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 24px;
          padding-bottom: 16px;
          border-bottom: 2px solid #f1f5f9;
        }

        .section-icon {
          width: 40px;
          height: 40px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 16px;
        }

        .section-header h5 {
          color: #1e293b;
          margin: 0;
          font-weight: 700;
          font-size: 20px;
        }

        .form-group-custom {
          margin-bottom: 24px;
        }

        .form-label-custom {
          display: flex;
          align-items: center;
          font-weight: 600;
          color: #374151;
          margin-bottom: 8px;
                    font-size: 14px;
        }

        .form-control-custom {
          border: 1.5px solid #e5e7eb;
          border-radius: 12px;
          padding: 12px 16px;
          font-size: 14px;
          transition: all 0.2s ease;
          background: #f9fafb;
        }

        .form-control-custom:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
          background: #ffffff;
        }

        .form-control-custom:hover:not(:focus) {
          border-color: #cbd5e1;
          background: #ffffff;
        }

        .form-text-custom {
          color: #64748b;
          font-size: 12px;
          margin-top: 6px;
          display: flex;
          align-items: center;
        }

        .knowledge-textarea,
        .code-textarea {
          font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 13px;
          line-height: 1.5;
        }

        .code-textarea {
          background: #1e293b;
          color: #e2e8f0;
          border-color: #334155;
        }

        .code-textarea:focus {
          background: #1e293b;
          color: #e2e8f0;
          border-color: #667eea;
        }

        .custom-switch-container {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: #f8fafc;
          border-radius: 12px;
          border: 2px solid #e2e8f0;
          margin-top: 8px;
        }

        .custom-switch {
          margin: 0;
        }

        .custom-switch .form-check-input {
          width: 48px;
          height: 24px;
          border-radius: 12px;
          background-color: #cbd5e1;
          border: none;
          cursor: pointer;
        }

        .custom-switch .form-check-input:checked {
          background-color: #10b981;
          border-color: #10b981;
        }

        .custom-switch .form-check-input:focus {
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        /* Reply in user's language checkbox border visibility */
        .reply-lang-check .form-check-input {
          border: 2px solid #94a3b8;
        }
        .reply-lang-check .form-check-input:checked {
          border-color: #10b981;
        }
        [data-bs-theme="dark"] .reply-lang-check .form-check-input {
          border-color: #64748b;
        }

        .switch-label {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
        }

        .status-icon {
          font-size: 16px;
        }

        .status-icon.active {
          color: #10b981;
        }

        .status-icon.inactive {
          color: #94a3b8;
        }

        .model-info {
          margin-top: 12px;
        }

        .model-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }

        .model-badges .badge {
          font-size: 11px;
          padding: 6px 12px;
          border-radius: 20px;
          font-weight: 600;
        }

        .chatbot-form-footer {
          background: var(--m-panel-bg);
          border: none;
          padding: 24px 32px;
          border-top: 1px solid var(--m-border);
        }

        .footer-actions {
          display: flex;
          gap: 16px;
          justify-content: flex-end;
          width: 100%;
        }

        .cancel-btn {
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          border: 2px solid var(--m-border);
          color: var(--m-text-muted);
          background: transparent;
          transition: all 0.2s ease;
        }

        .cancel-btn:hover:not(:disabled) {
          border-color: #cbd5e1;
          background: var(--m-chip-bg);
          color: var(--m-text-secondary);
        }

        .submit-btn {
          padding: 12px 24px;
          border-radius: 12px;
          font-weight: 600;
          background: linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%);
          border: none;
          color: #f8fafc;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
        }

        .submit-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #0891b2 0%, #4f46e5 100%);
          transform: translateY(-1px);
          box-shadow: 0 10px 28px rgba(2, 6, 23, 0.18);
        }

        .submit-btn:disabled {
          background: #e2e8f0;
          color: #94a3b8;
          transform: none;
          box-shadow: none;
        }

        /* Scrollbar Styling */
        .chatbot-form-body::-webkit-scrollbar {
          width: 8px;
        }

        .chatbot-form-body::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }

        .chatbot-form-body::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }

        .chatbot-form-body::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }

        /* Responsive Design */
        @media (max-width: 992px) {
          .chatbot-form-modal .modal-dialog {
            max-width: 95%;
            margin: 20px auto;
          }

          .chatbot-form-header {
            padding: 20px 24px;
          }

          .modal-title {
            font-size: 20px;
          }

          .modal-icon {
            width: 48px;
            height: 48px;
            font-size: 20px;
          }

          .chatbot-form-body {
            padding: 24px 20px;
            max-height: 60vh;
          }

          .tab-content-custom {
            padding: 24px 20px;
          }

          .section-header h5 {
            font-size: 18px;
          }

          .custom-nav-tabs {
            overflow-x: auto;
            flex-wrap: nowrap;
            padding: 6px;
          }

          .custom-nav-link {
            white-space: nowrap;
            padding: 10px 16px !important;
            font-size: 13px;
          }

          .chatbot-form-footer {
            padding: 20px 24px;
          }

          .footer-actions {
            flex-direction: column-reverse;
            gap: 12px;
          }

          .cancel-btn,
          .submit-btn {
            width: 100%;
            justify-content: center;
          }
        }

        @media (max-width: 576px) {
          .chatbot-form-modal .modal-dialog {
            max-width: 100%;
            margin: 10px;
          }

          .chatbot-form-header {
            padding: 16px 20px;
            flex-direction: column;
            gap: 16px;
            text-align: center;
          }

          .close-btn {
            position: absolute;
            top: 16px;
            right: 20px;
            width: 32px;
            height: 32px;
            font-size: 16px;
          }

          .modal-title {
            font-size: 18px;
          }

          .chatbot-form-body {
            padding: 20px 16px;
            max-height: 50vh;
          }

          .tab-content-custom {
            padding: 20px 16px;
          }

          .section-header {
            flex-direction: column;
            text-align: center;
            gap: 8px;
          }

          .form-group-custom {
            margin-bottom: 20px;
          }

          .model-badges {
            justify-content: center;
          }

          .custom-switch-container {
            flex-direction: column;
            text-align: center;
            gap: 12px;
          }
        }

        /* Dark Theme Support */
        [data-bs-theme="dark"] .chatbot-form-body {
          background: #0f172a;
        }

        [data-bs-theme="dark"] .tab-content-custom {
          background: #0b1220;
          border-color: #1f2937;
        }

        [data-bs-theme="dark"] .custom-nav-tabs {
          background: #0b1220;
          border-color: #1f2937;
        }

        [data-bs-theme="dark"] .custom-nav-link {
          color: #cbd5e1 !important;
        }

        [data-bs-theme="dark"] .custom-nav-link:hover {
          background: #172135 !important;
          color: #e5e7eb !important;
        }

        [data-bs-theme="dark"] .section-header {
          border-color: #334155;
        }

        [data-bs-theme="dark"] .section-header h5 {
          color: #f8fafc;
        }

        [data-bs-theme="dark"] .form-label-custom {
          color: #e2e8f0;
        }

        [data-bs-theme="dark"] .form-control-custom {
          background: #111827;
          border-color: #374151;
          color: #e5e7eb;
        }

        [data-bs-theme="dark"] .form-control-custom:focus {
          background: #0b1220;
          border-color: #4f46e5;
        }

        [data-bs-theme="dark"] .form-control-custom:hover:not(:focus) {
          background: #111827;
          border-color: #4b5563;
        }

        [data-bs-theme="dark"] .form-text-custom {
          color: #94a3b8;
        }

        [data-bs-theme="dark"] .custom-switch-container {
          background: #0b1220;
          border-color: #1f2937;
        }

        [data-bs-theme="dark"] .chatbot-form-footer {
          background: #1e293b;
          border-color: #334155;
        }

        [data-bs-theme="dark"] .cancel-btn {
          border-color: #475569;
          color: #cbd5e1;
        }

        [data-bs-theme="dark"] .cancel-btn:hover:not(:disabled) {
          background: #334155;
          border-color: #64748b;
          color: #f8fafc;
        }

        /* Loading Animation */
        .spinner-border-sm {
          width: 1rem;
          height: 1rem;
        }

        /* Badge Animations */
        .badge {
          transition: all 0.2s ease;
        }

        .badge:hover {
          transform: translateY(-1px);
        }

        /* Source list modern layout */
        .source-item {
          background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(255,255,255,0.85));
          border: 1px solid rgba(226,232,240,0.6);
          border-radius: 14px;
          margin-bottom: 10px;
          transition: transform .12s ease, box-shadow .12s ease, background .2s ease;
        }
        .source-row {
          display: grid;
          grid-template-columns: 1.2fr 1fr auto;
          align-items: center;
          gap: 10px;
        }
        @media (max-width: 768px) {
          .source-row { grid-template-columns: 1fr; gap: 6px; }
        }
        .source-main {
          display: flex; align-items: center; gap: 10px; min-width: 0;
        }
        .source-title {
          font-weight: 600; color: #0f172a; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .source-meta { display: flex; gap: 10px; color: #64748b; font-size: 12px; }
        .source-actions { display: flex; gap: 8px; align-items: center; }
        .source-type { background: #e2e8f0 !important; color: #0f172a; border: 1px solid #cbd5e1; }
        .source-item:hover { transform: translateY(-1px); box-shadow: 0 8px 22px rgba(102,126,234,0.12); background: linear-gradient(180deg, rgba(255,255,255,0.95), rgba(247,248,255,0.9)); }
        .icon-btn { padding: 6px 10px; border-radius: 10px; }
        .icon-btn:hover { background: rgba(239, 68, 68, 0.08); }
        [data-bs-theme="dark"] .source-item { background: #0b1220; border-color: #1f2937; }
        [data-bs-theme="dark"] .source-title { color: #e5e7eb; }
        [data-bs-theme="dark"] .source-meta { color: #9ca3af; }
        [data-bs-theme="dark"] .source-type { background: #374151 !important; color: #e5e7eb; border-color: #4b5563; }
        [data-bs-theme="dark"] .source-item:hover { box-shadow: 0 10px 28px rgba(0,0,0,0.35); background: linear-gradient(180deg, rgba(15,23,42,0.9), rgba(17,24,39,0.9)); }

        /* Form Validation States */
        .form-control-custom.is-invalid {
          border-color: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
        }

        .form-control-custom.is-valid {
          border-color: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
        }

        /* Tooltip Styles */
        .tooltip-custom {
          position: relative;
          cursor: help;
        }

        .tooltip-custom:hover::after {
          content: attr(data-tooltip);
          position: absolute;
          bottom: 100%;
          left: 50%;
          transform: translateX(-50%);
          background: #1e293b;
          color: white;
          padding: 8px 12px;
          border-radius: 6px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 1000;
        }

        /* Focus Management */
        .form-control-custom:focus-visible {
          outline: 2px solid #667eea;
          outline-offset: 2px;
        }

        /* Animation for tab switching */
        .tab-pane {
          transition: opacity 0.15s ease-in-out;
        }

        .tab-pane:not(.active) {
          opacity: 0;
        }

        .tab-pane.active {
          opacity: 1;
        }

        /* Theme-aware variables and overrides for minimal-modern modal UI */
        .chatbot-form-modal {
          --m-body-bg: #f8fafc;
          --m-panel-bg: #ffffff;
          --m-border: #e5e7eb;
          --m-text-primary: #0f172a;
          --m-text-secondary: #334155;
          --m-text-muted: #64748b;
          --m-control-bg: #f9fafb;
          --m-shadow: 0 8px 24px rgba(2, 6, 23, 0.06);
          --m-chip-bg: rgba(2, 6, 23, 0.04);
          --m-chip-border: rgba(2, 6, 23, 0.08);
        }

        [data-bs-theme="dark"] .chatbot-form-modal {
          --m-body-bg: #0f172a;
          --m-panel-bg: #0b1220;
          --m-border: #334155;
          --m-text-primary: #f8fafc;
          --m-text-secondary: #cbd5e1;
          --m-text-muted: #94a3b8;
          --m-control-bg: #111827;
          --m-shadow: 0 10px 28px rgba(0, 0, 0, 0.35);
          --m-chip-bg: rgba(255, 255, 255, 0.05);
          --m-chip-border: rgba(255, 255, 255, 0.12);
        }

        .chatbot-form-body {
          background: var(--m-body-bg) !important;
        }

        .tab-content-custom,
        .custom-nav-tabs {
          background: var(--m-panel-bg) !important;
          border-color: var(--m-border) !important;
          box-shadow: var(--m-shadow) !important;
        }

        .custom-nav-link {
          color: var(--m-text-muted) !important;
        }
        .custom-nav-link:hover {
          background: var(--m-chip-bg) !important;
          color: var(--m-text-secondary) !important;
        }

        .form-label-custom {
          color: var(--m-text-secondary) !important;
        }

        /* Inputs and selects */
        .chatbot-form-modal .form-control-custom,
        .chatbot-form-modal .form-select.form-control-custom,
        .chatbot-form-modal .form-control {
          background: var(--m-control-bg) !important;
          border-color: var(--m-border) !important;
          color: var(--m-text-primary) !important;
        }
        .chatbot-form-modal .form-control-custom:focus,
        .chatbot-form-modal .form-select.form-control-custom:focus,
        .chatbot-form-modal .form-control:focus {
          background: var(--m-panel-bg) !important;
          border-color: #6366f1 !important;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.14) !important;
        }
        .chatbot-form-modal .form-control-custom:hover:not(:focus),
        .chatbot-form-modal .form-select.form-control-custom:hover:not(:focus) {
          background: var(--m-panel-bg) !important;
        }
        .chatbot-form-modal .form-control-custom::placeholder,
        .chatbot-form-modal .form-control::placeholder {
          color: var(--m-text-muted) !important;
          opacity: 0.8;
        }

        /* Glass card dark adaptation */
        [data-bs-theme="dark"] .chatbot-form-modal .glass-card {
          background: rgba(2, 6, 23, 0.6) !important;
          border-color: rgba(148, 163, 184, 0.18) !important;
          box-shadow: var(--m-shadow) !important;
        }

        /* Gradient header fine-tuned for dark */
        [data-bs-theme="dark"] .chatbot-form-modal .gradient-card-header {
          background: linear-gradient(135deg, #0ea5e9 0%, #4f46e5 100%) !important;
          color: #e5e7eb !important;
        }

        /* Detail chips inside cards */
        .chatbot-form-modal .chip,
        .chatbot-form-modal .custom-switch-container {
          background: var(--m-chip-bg) !important;
          border-color: var(--m-chip-border) !important;
        }

        /* ListGroup items subtle separation in both themes */
        .chatbot-form-modal .list-group-item {
          background: var(--m-panel-bg);
          border-color: var(--m-border);
          color: var(--m-text-primary);
        }
      `}</style>
    </Modal>
  );
};

export default AIChatbotFormModal;
